import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FileSpreadsheet, FileText } from "lucide-react";
import { api, errorMessage, formatDate } from "@/lib/api";
import { DataTable, download } from "@/lib/admin";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { PageHead } from "@/pages/admin/AdminLayout";
import { FilterBar, ALL_FILTERS, SEDE_FILTERS, useFilterOptions } from "@/components/admin/FilterBar";
import { Notice } from "@/components/form/FormFeedback";
import { Loading } from "@/components/layout/RequireAuth";
import { ReportButtons } from "@/components/admin/ReportButtons";
import { usePageTitle } from "@/components/layout/AppLayout";

const filterQs = (params, drop = ["page", "sort", "dir"]) => { const q = new URLSearchParams(params); drop.forEach((k) => q.delete(k)); return q; };

export const ExportButtons = ({ kind, params, version }) => {
    const { user } = useAuth();
    const [msg, setMsg] = useState("");
    if (!can(user, "download", "C")) return null;
    const go = async (format) => {
        const q = filterQs(params, kind === "companies" ? ["page", "sort", "dir"] : ["page", "sort", "dir", "q"]);
        q.set("format", format);
        if (version) q.set("version", version);
        try { setMsg(`File scaricato: ${await download(`/admin/exports/${kind}?${q}`)}`); } catch (e) { setMsg("Esportazione non riuscita, riprova"); }
    };
    const label = kind === "companies" ? "" : " risultati assessment";
    return (
        <div className="flex flex-wrap items-center gap-2">
            <button type="button" className="btn-primary py-2" onClick={() => go("xlsx")} data-testid={`export-${kind}-xlsx`}><FileSpreadsheet aria-hidden="true" size={18} /> Esporta Excel{label}</button>
            <button type="button" className="btn-secondary py-2" onClick={() => go("csv")} data-testid={`export-${kind}-csv`}><FileText aria-hidden="true" size={18} /> Esporta CSV{label}</button>
            <p role="status" aria-live="polite" className="text-sm w-full" data-testid={`export-${kind}-status`}>{msg}</p>
        </div>
    );
};

const COLUMNS = [
    { key: "ragione_sociale", label: "Ragione sociale" }, { key: "partita_iva", label: "Partita IVA" }, { key: "provincia", label: "Provincia" },
    { key: "settore", label: "Settore" }, { key: "spin", label: "Sede SPIN" }, { key: "score", label: "Ultimo punteggio" }, { key: "level", label: "Livello" },
    { key: "last_date", label: "Data ultimo assessment", render: (r) => formatDate(r.last_date) || "—" },
    { key: "consent", label: "Consenso AI", render: (r) => (r.consent ? "Sì" : "No") },
    { key: "email_confirmed", label: "Email confermata", render: (r) => (r.email_confirmed ? "Sì" : "No") },
];

export const Aziende = () => {
    usePageTitle("Aziende");
    const { user } = useAuth();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const [q, setQ] = useState(params.get("q") || "");
    const [d, setD] = useState(null);
    const [error, setError] = useState("");
    const sort = { key: params.get("sort") || "ragione_sociale", dir: params.get("dir") || "asc" };
    const page = Number(params.get("page") || 1);

    useEffect(() => {
        api.get(`/admin/companies?${params}`).then(({ data }) => setD(data)).catch((e) => setError(errorMessage(e)));
    }, [params]);
    useEffect(() => {
        const t = setTimeout(() => {
            if ((params.get("q") || "") === q) return;
            const n = new URLSearchParams(params); q ? n.set("q", q) : n.delete("q"); n.delete("page"); setParams(n);
        }, 300);
        return () => clearTimeout(t);
    }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = (patch) => { const n = new URLSearchParams(params); Object.entries(patch).forEach(([k, v]) => n.set(k, v)); setParams(n); };
    const onSort = (key) => set({ sort: key, dir: sort.key === key && sort.dir === "asc" ? "desc" : "asc", page: 1 });
    const pages = d ? Math.max(1, Math.ceil(d.total / 25)) : 1;

    return (
        <>
            <PageHead title="Aziende"><div className="flex flex-col items-end gap-2"><ReportButtons section="aziende" /><ExportButtons kind="companies" params={params} /></div></PageHead>
            <div className="mb-4 max-w-md">
                <label htmlFor="company-search" className="edi-label">Cerca per ragione sociale o Partita IVA</label>
                <input id="company-search" type="search" className="edi-input" value={q} onChange={(e) => setQ(e.target.value)} data-testid="company-search-input" />
            </div>
            <FilterBar keys={user.role !== "admin_sede" ? ALL_FILTERS : SEDE_FILTERS} version={false} />
            {error && <Notice kind="error" role="alert">{error}</Notice>}
            {!d ? <Loading /> : (
                <>
                    <p className="mb-2 font-semibold" role="status" data-testid="company-total">{d.total} aziende trovate</p>
                    <DataTable caption="Elenco aziende" testId="companies-table" rows={d.items} columns={COLUMNS} sort={sort} onSort={onSort}
                        onRowClick={(r) => navigate(`/amministrazione/aziende/${r.id}`)} empty="Nessuna azienda trovata" />
                    <nav aria-label="Paginazione" className="mt-4 flex items-center gap-3">
                        <button type="button" className="btn-secondary py-2" disabled={page <= 1} onClick={() => set({ page: page - 1 })} data-testid="page-prev">Precedente</button>
                        <span data-testid="page-indicator">Pagina {page} di {pages}</span>
                        <button type="button" className="btn-secondary py-2" disabled={page >= pages} onClick={() => set({ page: page + 1 })} data-testid="page-next">Successiva</button>
                    </nav>
                </>
            )}
        </>
    );
};

export const Esporta = () => {
    usePageTitle("Esporta");
    const [params] = useSearchParams();
    const opts = useFilterOptions();
    const [version, setVersion] = useState("");
    return (
        <>
            <PageHead title="Esporta" />
            <FilterBar keys={SEDE_FILTERS} version={false} />
            <section className="edi-card p-5 mb-6" aria-labelledby="exp-c"><h2 id="exp-c" className="text-xl font-bold mb-3">Aziende</h2><ExportButtons kind="companies" params={params} /></section>
            <section className="edi-card p-5" aria-labelledby="exp-a">
                <h2 id="exp-a" className="text-xl font-bold mb-3">Risultati assessment</h2>
                <label htmlFor="exp-version" className="edi-label">Versione questionario</label>
                <select id="exp-version" className="edi-input max-w-xs mb-3" value={version} onChange={(e) => setVersion(e.target.value)} data-testid="export-version-select">
                    <option value="">Versione attiva</option>
                    {opts?.versions.map((v) => <option key={v.version} value={v.version}>{`Versione ${v.version}`}</option>)}
                </select>
                <ExportButtons kind="assessments" params={params} version={version} />
            </section>
        </>
    );
};
